import React, { useEffect, useState } from 'react';
import { Printer, FileCheck, AlertTriangle } from 'lucide-react';
import api from '../services/api';

// Declaración responsable del sistema informático de facturación (VeriFactu):
// enseña la de la versión en uso, tal como la compone el servidor
// (server/services/verifactu/declaracion.js), y deja ver e imprimir las de las
// versiones anteriores, guardadas en verifactu_declaraciones (una fila por
// versión). Mientras el programa no cumpla el reglamento, la versión en uso
// llega como BORRADOR sin fecha ni lugar de suscripción, y aquí se dice.
// La pantalla solo enseña, en el orden del art. 15.1 de la Orden
// HAC/1177/2024; no compone ni cambia nada.

const fmtFecha = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '—';
};

const siNo = (v) => (v === 'S' ? 'S - Sí' : 'N - No');

const Apartado = ({ rotulo, children }) => (
  <div className="dr-apartado">
    <div className="dr-rotulo">{rotulo}</div>
    <div className="dr-valor">{children}</div>
  </div>
);

const DeclaracionResponsable = () => {
  const [enUso, setEnUso] = useState(null);
  const [versiones, setVersiones] = useState([]);
  const [vista, setVista] = useState(null);
  const [error, setError] = useState('');
  const [cargando, setCargando] = useState(true);

  // Marca en <body> para que las reglas de impresión del CSS (que esconden el
  // menú y dejan solo la declaración) actúen SOLO en esta pantalla.
  useEffect(() => {
    document.body.classList.add('imprimir-declaracion');
    return () => document.body.classList.remove('imprimir-declaracion');
  }, []);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const res = await api.get('/verifactu/declaracion');
        if (!vivo) return;
        setEnUso(res.data.enUso);
        setVista(res.data.enUso);
        setVersiones(res.data.versiones || []);
      } catch (e) {
        if (vivo) setError(e.response?.data?.message || e.message);
      } finally {
        if (vivo) setCargando(false);
      }
    })();
    return () => { vivo = false; };
  }, []);

  const verVersion = async (version) => {
    setError('');
    if (enUso && version === enUso.version) {
      setVista(enUso);
      return;
    }
    try {
      const res = await api.get(`/verifactu/declaracion/${encodeURIComponent(version)}`);
      setVista(res.data);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    }
  };

  const d = vista?.datos;
  const r = d?.rotulos;

  // Lista para la tarjeta de versiones: la de uso primero (aunque sea un
  // borrador sin guardar) y después las suscritas.
  const lista = enUso
    ? [
        { version: enUso.version, enUso: true, borrador: enUso.borrador, fechaSuscripcion: enUso.fechaSuscripcion },
        ...versiones.filter((v) => v.version !== enUso.version).map((v) => ({ ...v, borrador: false })),
      ]
    : versiones;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Declaración responsable</h1>
          <p className="page-subtitle">Del sistema informático de facturación (VeriFactu), una por versión del programa</p>
        </div>
        {d && (
          <button type="button" className="btn btn-primary" onClick={() => window.print()}>
            <Printer size={16} /> Imprimir
          </button>
        )}
      </div>

      {cargando && <div className="card">Cargando…</div>}

      {error && (
        <div className="card dr-no-print" style={{ marginBottom: 20, color: 'var(--danger)' }}>
          {error}
        </div>
      )}

      {vista?.borrador && (
        <div className="card dr-borrador">
          <AlertTriangle size={18} />
          <div>
            <strong>Borrador sin suscribir.</strong> Pendiente de que el programa cumpla el reglamento:
            la fecha y el lugar de suscripción (letra l) quedan sin poner hasta entonces. Lo que se ve es lo que
            declararía esta versión ({vista.version}) tal como está hoy.
          </div>
        </div>
      )}

      {d && (
        <article className="card declaracion">
          <h2 className="declaracion-titulo">{r.titulo}</h2>
          <Apartado rotulo={r.a}>{d.nombre}</Apartado>
          <Apartado rotulo={r.b}>{d.codigo}</Apartado>
          <Apartado rotulo={r.c}>{d.version}</Apartado>
          <Apartado rotulo={r.d}>
            {d.componentes.map((p, i) => <p key={i}>{p}</p>)}
          </Apartado>
          <Apartado rotulo={r.e}>{siNo(d.soloVerifactu)}</Apartado>
          <Apartado rotulo={r.f}>{siNo(d.multiOT)}</Apartado>
          <Apartado rotulo={r.g}><p>{d.firma}</p></Apartado>
          <Apartado rotulo={r.h}>{d.productorNombre}</Apartado>
          <Apartado rotulo={r.i}>{d.productorNif}</Apartado>
          <Apartado rotulo={r.j}>{d.productorDireccion}</Apartado>
          <div className="dr-apartado">
            <div className="dr-rotulo dr-rotulo-k">{r.k}</div>
          </div>
          <Apartado rotulo={r.lFecha}>
            <span className={d.pendiente ? 'dr-pendiente' : ''}>{d.fechaTexto}</span>
          </Apartado>
          <Apartado rotulo={r.lLugar}>
            <span className={d.pendiente ? 'dr-pendiente' : ''}>{d.lugar}</span>
          </Apartado>
          {(d.anexoContacto || d.anexoWeb) && (
            <div className="dr-anexo">
              <div className="dr-anexo-titulo">{r.anexo}</div>
              {d.anexoContacto && <Apartado rotulo={r.anexoA}>{d.anexoContacto}</Apartado>}
              {d.anexoWeb && <Apartado rotulo={r.anexoB}>{d.anexoWeb}</Apartado>}
            </div>
          )}
        </article>
      )}

      {lista.length > 0 && (
        <div className="card dr-no-print dr-versiones">
          <h2 className="card-title">Versiones</h2>
          <p className="page-subtitle" style={{ marginBottom: 12 }}>
            Cada versión del programa tiene su declaración, suscrita el día en que se puso en marcha. Se conservan todas.
          </p>
          <ul>
            {lista.map((v) => (
              <li key={v.version}>
                <button
                  type="button"
                  className={`dr-version ${vista?.version === v.version ? 'activa' : ''}`}
                  onClick={() => verVersion(v.version)}
                >
                  <FileCheck size={14} />
                  <span>Versión {v.version}</span>
                  <span className="dr-version-fecha">
                    {v.borrador ? 'borrador sin suscribir' : `suscrita el ${fmtFecha(v.fechaSuscripcion)}`}
                  </span>
                  {v.enUso && <span className="dr-version-uso">en uso</span>}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default DeclaracionResponsable;
